import { useState } from "react";
import { toast } from "sonner";
import { Download, FileArchive, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, downloadBlobResponse } from "@/lib/api";
import { bolehKelolaSurvei, bolehLihatSurvei } from "@/lib/roles";
import {
    LINGKUP_SEMUA, paramEkspor, pesanGagalUnduhSurvei, TEKS_BANTU_UNDUH_SURVEI,
    TEKS_KONFIRMASI_ISIAN_TERBUKA, TEKS_PERIODE_DIBUKA_UNDUH, teksLingkup, KELOMPOK_KEPNAKES,
} from "@/lib/survei";

// Tombol "Unduh Excel" hasil survei (GET /survei/periode/{id}/ekspor). Hanya utk peran yg boleh melihat hasil; server
// tetap menolak peran lain (403). Isian terbuka KHUSUS Super Admin (Admin UFP: opsinya tidak ada, server menolak 403), default
// OFF dan wajib dikonfirmasi saat dinyalakan.
// `periodeAktif` (periode masih dibuka): server hanya memberi lembar Info + Cakupan jenjang dan mengabaikan opsi
// isian terbuka -- kotaknya dinonaktifkan, teks bantu diganti penjelasan, tombol Unduh Excel tetap aktif (ZIP disembunyikan).
// `lingkup` mengikuti pilihan di tab Hasil: "prodi" -> berkas prodi terpilih (nonaktif sampai prodi yg memenuhi syarat
// dipilih); isian terbuka hanya utk "semua". "Unduh semua prodi (ZIP)" = satu berkas per prodi yg memenuhi syarat.
// `kelompok` "kepnakes" (PR #113): lingkup "institusi" (prodiId = id institusi terpilih), ZIP per institusi; nama berkas.
export default function SurveiUnduhExcel({
    periodeId, tanpaLurus, kosong, periodeAktif = false, lingkup = LINGKUP_SEMUA, prodiId = "", prodiBoleh = false,
    kelompok = "kedokteran",
}) {
    const tl = teksLingkup(kelompok);
    const awalanBerkas = kelompok === KELOMPOK_KEPNAKES ? "survei-kepnakes" : "survei-kepuasan";
    const { user } = useAuth();
    const [terbuka, setTerbuka] = useState(false);
    const [konfirmasi, setKonfirmasi] = useState(false);
    const [sibuk, setSibuk] = useState(false);
    if (!bolehLihatSurvei(user?.role) || !periodeId) return null;
    const opsiTerbuka = bolehKelolaSurvei(user?.role);
    const bolehTerbuka = opsiTerbuka && !periodeAktif && lingkup === LINGKUP_SEMUA;
    const sertakanTerbuka = terbuka && bolehTerbuka; // periode dibuka / lingkup prodi: tidak pernah ikut diminta
    const jenisExcel = periodeAktif ? LINGKUP_SEMUA : lingkup;
    const excelSiap = jenisExcel !== tl.lingkupPilih || (!!prodiId && prodiBoleh);

    const ubahTerbuka = (nilai) => {
        if (nilai === true) setKonfirmasi(true); // dinyalakan hanya lewat tombol "Lanjut" di konfirmasi
        else setTerbuka(false);
    };

    const unduh = async (jenis) => {
        setSibuk(jenis);
        try {
            const res = await api.get(`/survei/periode/${periodeId}/ekspor`, {
                params: paramEkspor(jenis, { prodiId, tanpaLurus, sertakanTerbuka }), responseType: "blob",
            });
            downloadBlobResponse(res, jenis === "zip" ? `${awalanBerkas}.zip` : `${awalanBerkas}.xlsx`);
        } catch (e) {
            toast.error(pesanGagalUnduhSurvei(e?.response?.status));
        } finally {
            setSibuk(false);
        }
    };

    return (
        <div className="space-y-2 rounded-lg border border-slate-200 p-4" data-testid="survei-unduh">
            <div className="flex flex-wrap items-center gap-4">
                <Button onClick={() => unduh(jenisExcel)} disabled={!!sibuk || !excelSiap} aria-busy={sibuk === jenisExcel}
                        data-testid="survei-unduh-excel" className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]">
                    {sibuk === jenisExcel ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="mr-2 h-4 w-4" aria-hidden="true" />}
                    {sibuk === jenisExcel ? "Menyiapkan berkas…" : "Unduh Excel"}
                </Button>
                {!periodeAktif && (
                    <Button variant="outline" onClick={() => unduh("zip")} disabled={!!sibuk} aria-busy={sibuk === "zip"} data-testid="survei-unduh-zip">
                        {sibuk === "zip" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <FileArchive className="mr-2 h-4 w-4" aria-hidden="true" />}
                        {sibuk === "zip" ? "Menyiapkan berkas…" : tl.zip}
                    </Button>
                )}
                {opsiTerbuka && lingkup === LINGKUP_SEMUA && (
                    <div className="flex items-center gap-2">
                        <Checkbox id="survei-sertakan-terbuka" checked={sertakanTerbuka} onCheckedChange={ubahTerbuka} disabled={!!sibuk || !bolehTerbuka}
                                  data-testid="survei-sertakan-terbuka" />
                        <Label htmlFor="survei-sertakan-terbuka">Sertakan isian terbuka</Label>
                    </div>
                )}
            </div>
            {!excelSiap && (
                <p className="text-xs text-slate-600" data-testid="survei-unduh-pilih-prodi">{tl.pilihDulu}</p>
            )}
            <p className="text-xs text-slate-500" data-testid="survei-unduh-bantu">{periodeAktif ? TEKS_PERIODE_DIBUKA_UNDUH : TEKS_BANTU_UNDUH_SURVEI}</p>
            {kosong && (
                <p className="text-xs font-semibold text-slate-600" data-testid="survei-unduh-kosong">
                    {periodeAktif ? "Belum ada responden." : "Belum ada responden. Berkas hanya berisi lembar Info."}
                </p>
            )}

            <AlertDialog open={konfirmasi} onOpenChange={(o) => !o && setKonfirmasi(false)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Sertakan isian terbuka?</AlertDialogTitle>
                        <AlertDialogDescription>{TEKS_KONFIRMASI_ISIAN_TERBUKA}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="survei-terbuka-batal">Batal</AlertDialogCancel>
                        <AlertDialogAction data-testid="survei-terbuka-lanjut" onClick={() => { setTerbuka(true); setKonfirmasi(false); }}>
                            Lanjut
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
